import { useEffect } from "react";

import {
  GetCoachingProgramsQuery,
  useGetCoachingProgramsQuery,
} from "@/graphql";

import { findFirstAvailableSession } from "./find-first-available-session";

type Program = GetCoachingProgramsQuery["coachingPrograms"]["programs"][number];
type Session = Program["sessions"][number];
type Product = Session["product"];
type CoachProduct = Program["coaches"][number]["coachProduct"];

interface UseProgramsProps {
  coachId: string;
  showPrograms: boolean;
  programsInitialized: boolean;
  workspaceId: string;
  clientId: string;
  onSelectProduct: (product: Product, coachProduct: CoachProduct) => void;
  onSelectSession: (session: Session) => void;
  onSelectProgramId: (programId: string) => void;
  onSetShowProducts: (showProducts: boolean) => void;
  onSetWarningMessage: (message: string | null) => void;
  onSetShowPrograms: (showPrograms: boolean) => void;
  onSetProgramsInitialized: (initialized: boolean) => void;
}

/**
 * Custom hook for managing coaching programs in the scheduling interface.
 *
 * This hook handles the complex logic of:
 * - Fetching coaching programs for a specific coach
 * - Auto-selecting the first available program and bookable session
 * - Managing program visibility based on availability
 * - Displaying appropriate warning messages for edge cases
 * - Preventing re-initialization on subsequent renders
 *
 * @param props - Configuration object containing coach ID, state flags, and callback functions
 * @returns Object containing the array of coaching programs
 */
export function usePrograms({
  coachId,
  showPrograms,
  programsInitialized,
  workspaceId,
  clientId,
  onSelectSession,
  onSelectProduct,
  onSelectProgramId,
  onSetShowProducts,
  onSetWarningMessage,
  onSetShowPrograms,
  onSetProgramsInitialized,
}: UseProgramsProps): { programs: Program[] } {
  // Fetch coaching programs for the specified coach
  // Skip the query if we have a sessionId (direct booking) or if programs are hidden
  const { data, loading } = useGetCoachingProgramsQuery({
    variables: {
      coachId,
      workspaceId,
      clientId,
    },
    skip: !showPrograms,
  });

  const programs = data?.coachingPrograms.programs || [];

  useEffect(() => {
    if (programs.length === 0) return;

    // Nice way to debug the results of the query
    // console.table(
    //   programs.map((p) => ({
    //     Program: p.programName,
    //     CoachInProgram: p.isCoachInProgram ? "✅ YES" : "❌ NO",
    //     ID: p.programId,
    //     AllSessionsBooked: p.areAllSessionsBooked ? "✅ YES" : "❌ NO",
    //   })),
    // );
    // console.log(programs);
  }, [programs]);

  /**
   * Main initialization effect that runs once when data loads.
   * Handles three main scenarios:
   * 1. No programs exist - hide programs section
   * 2. Programs available with bookable sessions - auto-select first available
   * 3. Programs exist but all sessions booked or coach unavailable - show warnings
   */
  useEffect(() => {
    // Prevent multiple initializations and wait for data to load
    if (loading || programsInitialized) return;

    // Scenario 1: No programs exist at all
    if (programs.length === 0) {
      onSetShowPrograms(false);
      onSetProgramsInitialized(true);
      return;
    }

    const { hasAvailablePrograms, isBookedForAllAvailableSessions } =
      data?.coachingPrograms || {};

    // Scenario 2: Coach has available programs with bookable sessions
    if (hasAvailablePrograms && !isBookedForAllAvailableSessions) {
      const { session, coachProduct } = findFirstAvailableSession(programs);
      if (session && coachProduct) {
        onSelectProgramId(session.programId);
        onSelectSession(session);
        onSelectProduct(session.product, coachProduct);
      } else {
        // No auto-bookable sessions found, but select first program for better UX
        onSelectProgramId(programs[0].programId);
      }

      onSetShowProducts(false);
      onSetWarningMessage(null);
      onSetProgramsInitialized(true);
      return;
    }

    // Scenario 3: Coach has issues that prevent booking
    if (!hasAvailablePrograms) {
      onSetWarningMessage(
        "This coach isn't available in any of your current programs. This session cannot contribute to program progress.",
      );
    } else if (isBookedForAllAvailableSessions) {
      onSetWarningMessage(
        "You have already booked all of the assigned sessions in the active program. This session won't contribute to program progress.",
      );
    }

    onSetProgramsInitialized(true);
  }, [programs, loading, programsInitialized]);

  return { programs };
}
